import axios from "axios";
import { useAuthStore } from "../store/authStore";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect if it's a login attempt failure
    if (error.response?.status === 401 && !error.config.url.includes('/auth/login')) {
      useAuthStore.getState().logout();
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data) => api.post("/auth/login", data),
  register: (data) => api.post("/auth/register", data),
  forgotPassword: (data) => api.post("/auth/forgot-password", data),
  resetPassword: (data) => api.post("/auth/reset-password", data),
  getMe: () => api.get("/auth/me"),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get("/dashboard/stats"),
};

// Customers API
export const customersAPI = {
  getAll: (search) => api.get("/customers", { params: { search } }),
  getPaged: (params) => api.get("/customers/paged", { params }),
  getStats: () => api.get("/customers/stats"),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post("/customers", data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  getContracts: (id) => api.get(`/customers/${id}/contracts`),
};

// Contracts API
export const contractsAPI = {
  getAll: (params) => api.get("/contracts", { params }),
  getPaged: (params) => api.get("/contracts/paged", { params }),
  getCritical: () => api.get("/contracts/critical"),
  getById: (id) => api.get(`/contracts/${id}`),
  checkUniqueness: (params) => api.get("/contracts/check-uniqueness", { params }),
  create: (data) => api.post("/contracts", data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  renew: (id, data) => api.post(`/contracts/${id}/renew`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  uploadFile: (id, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/contracts/${id}/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  exportExcel: (params) =>
    api.get("/contracts/export/excel", { params, responseType: "blob" }),
  exportCSV: (params) => api.get("/contracts/export/csv", { params, responseType: "blob" }),
  exportPDF: (params) => api.get("/contracts/export/pdf", { params, responseType: "blob" }),
  downloadFile: (url) => api.get(url, { responseType: "blob" }),
  deleteFile: (contractId, fileId) => api.delete(`/contracts/${contractId}/files/${fileId}`),
};

// Companies API
export const companiesAPI = {
  getAll: () => api.get("/companies"),
  create: (data) => api.post("/companies", data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
};

// Contract Types API
export const contractTypesAPI = {
  getAll: () => api.get("/contract-types"),
  create: (data) => api.post("/contract-types", data),
  update: (id, data) => api.put(`/contract-types/${id}`, data),
  delete: (id) => api.delete(`/contract-types/${id}`),
};

// Todos API
export const todosAPI = {
  getAll: () => api.get("/todos"),
  create: (data) => api.post("/todos", data),
  update: (id, data) => api.put(`/todos/${id}`, data),
  delete: (id) => api.delete(`/todos/${id}`),
};

// Settings API
export const settingsAPI = {
  get: () => api.get("/settings"),
  update: (data) => api.put("/settings", data),
  downloadBackup: (includeFiles = false, onDownloadProgress) =>
    api.get("/settings/backup", {
      params: includeFiles ? { include_files: true } : {},
      responseType: "blob",
      onDownloadProgress,
    }),
};

export default api;
